import { Component, OnDestroy, OnInit } from '@angular/core';
import { NavigationEnd, Router, RouterLink, RouterLinkActive, RouterOutlet } from '@angular/router';
import {CommonModule} from "@angular/common";
import {MatToolbarModule} from "@angular/material/toolbar";
import {MatIconModule} from "@angular/material/icon";
import {MatButtonModule} from "@angular/material/button";
import {HttpClientModule} from "@angular/common/http";
import { Meta, Title } from '@angular/platform-browser';
import { filter, Subscription } from 'rxjs';

let FooterComponent;

@Component({
  selector: 'app-root',
  standalone: true,
  imports: [
    RouterOutlet,
    RouterLinkActive,
    RouterLink,
    CommonModule,
    MatToolbarModule,
    MatIconModule,
    MatButtonModule,
    HttpClientModule
  ],
  providers: [
  ],
  templateUrl: './app.component.html',
  styleUrl: './app.component.scss'
})
export class AppComponent implements OnInit, OnDestroy {
  title = 'weterynarz-mogilany';
  isMobileMenuOpen = false;
  readonly currentYear = new Date().getFullYear();
  private navigationSubscription?: Subscription;

  constructor(
    private readonly router: Router,
    private readonly titleService: Title,
    private readonly metaService: Meta
  ) {}

  ngOnInit(): void {
    this.updateSeoMeta();
    this.navigationSubscription = this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe(() => this.updateSeoMeta());
  }

  ngOnDestroy(): void {
    this.navigationSubscription?.unsubscribe();
  }

  toggleMobileMenu(): void {
    this.isMobileMenuOpen = !this.isMobileMenuOpen;
  }

  closeMobileMenu(): void {
    this.isMobileMenuOpen = false;
  }

  scrollToTop(event: MouseEvent): void {
    event.preventDefault();
    this.closeMobileMenu();

    if (this.router.url !== '/' && !this.router.url.startsWith('/?')) {
      void this.router.navigateByUrl('/').then(() => {
        window.requestAnimationFrame(() => {
          this.setHash('start');
          window.scrollTo({ top: 0, behavior: 'smooth' });
        });
      });
      return;
    }

    this.setHash('start');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  scrollToAnchor(anchorId: string, event: MouseEvent): void {
    event.preventDefault();
    this.closeMobileMenu();

    const scrollToTarget = () => {
      window.requestAnimationFrame(() => {
        window.requestAnimationFrame(() => {
          const target = document.getElementById(anchorId);
          if (!target) {
            return;
          }

          this.setHash(anchorId);

          const topbar = document.querySelector('.topbar') as HTMLElement | null;
          const branchSticky = document.querySelector('.branch-sticky') as HTMLElement | null;
          const topbarHeight = topbar?.getBoundingClientRect().height ?? 0;
          const branchStickyHeight = branchSticky?.getBoundingClientRect().height ?? 0;
          const offset = topbarHeight + branchStickyHeight + 12;

          const targetTop = target.getBoundingClientRect().top + window.scrollY - offset;
          window.scrollTo({
            top: Math.max(0, targetTop),
            behavior: 'smooth'
          });
        });
      });
    };

    if (this.router.url !== '/' && !this.router.url.startsWith('/?')) {
      void this.router.navigateByUrl('/').then(() => {
        scrollToTarget();
      });
      return;
    }

    scrollToTarget();
  }

  private setHash(anchorId: string): void {
    const path = `${window.location.pathname}${window.location.search}`;
    const nextUrl = anchorId ? `${path}#${anchorId}` : path;
    window.history.replaceState(null, '', nextUrl);
  }

  private updateSeoMeta(): void {
    const hostname = window.location.hostname.toLowerCase();
    const path = this.router.url.split('?')[0];

    const baseTitle = 'Przychodnia weterynaryjna - Waldemar Grzechnik';

    let locationPart = 'Świątniki Górne, Mogilany, Wieliczka i Małopolska';
    let locationKeywords = 'Świątniki Górne, Mogilany, Wieliczka, Małopolska';
    if (hostname.includes('swiatniki')) {
      locationPart = 'Świątniki Górne';
      locationKeywords = 'Świątniki Górne';
    } else if (hostname.includes('mogilany')) {
      locationPart = 'Mogilany';
      locationKeywords = 'Mogilany';
    } else if (hostname.includes('wieliczka')) {
      locationPart = 'Wieliczka';
      locationKeywords = 'Wieliczka';
    }

    const pagePrefix = this.getPagePrefix(path);
    const fullTitle = pagePrefix
      ? `${pagePrefix} | ${baseTitle} | ${locationPart}`
      : `${baseTitle} | ${locationPart}`;

    this.titleService.setTitle(fullTitle);

    const description = `Przychodnia weterynaryjna Waldemar Grzechnik - ${locationPart}. Konsultacje, szczepienia, chirurgia, diagnostyka obrazowa, badanie krwi zwierząt, stomatologia, opieka profilaktyczna oraz pomoc dla psów, kotów i zwierząt gospodarskich w Małopolsce.`;
    const keywords = [
      'weterynarz',
      'weterynarz Wieliczka',
      'weterynarz Małopolska',
      'przychodnia weterynaryjna',
      'gabinet weterynaryjny',
      'weterynarz wiejski',
      'weterynarz dla koni',
      'weterynarz dla bydła',
      'weterynarz dla krów',
      'weterynarz dla kóz',
      'weterynarz dla świń',
      'weterynarz dla owiec',
      'porody krów',
      'badanie krwi zwierząt',
      'małopolska',
      'opieka nad bydłem',
      'opieka nad zwierzętami gospodarskimi',
      'zwierzęta hodowlane',
      'weterynarz dla psa i kota',
      'szczepienia zwierząt',
      'chirurgia weterynaryjna',
      'stomatologia zwierząt',
      'diagnostyka weterynaryjna',
      locationKeywords
    ].join(', ');

    this.metaService.updateTag({ name: 'description', content: description });
    this.metaService.updateTag({ name: 'keywords', content: keywords });
  }

  private getPagePrefix(path: string): string {
    if (path.startsWith('/polityka-prywatnosci')) {
      return 'Polityka prywatności';
    }
    if (path.startsWith('/rodo')) {
      return 'RODO';
    }
    if (path.startsWith('/kontakt')) {
      return 'Kontakt';
    }
    if (path.startsWith('/about')) {
      return 'O nas';
    }
    return '';
  }
}
